import { type ReactNode, useMemo, useRef, useState } from 'react';
import {
  type FieldPath,
  type FieldValues,
  get,
  useController,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type Language, type LocalizedText, zLocalizedText } from '@/shared/api';
import { cn } from '@/shared/lib';
import { GLYPH } from '../glyphs';
import { Input } from '../Input';
import { SegmentedControl } from '../SegmentedControl';
import { useContentLanguages } from './contentLanguages';
import { FormField } from './FormField';
import { useRegisterFields } from './fieldRegistry';
import { emptyLocalizedText, localizedTextOf } from './localizedText';
import { isLanguageOf, nestedErrorMessage } from './useLocalizedInput';

/** Строка списка: ключ для React и текст LocalizedText. */
export interface TextListItem {
  key: string;
  text: LocalizedText;
}

let itemCounter = 0;

export function textListItem(text: LocalizedText): TextListItem {
  itemCounter += 1;
  return { key: `text-${itemCounter}`, text };
}

export function textListItemsOf(texts: readonly LocalizedText[]): TextListItem[] {
  return texts.map((text) => textListItem(text));
}

/** Строка списка без языка (`TextListField` с `plain`): значения настроек интеграции. */
export interface PlainTextListItem {
  key: string;
  text: string;
}

export function plainTextListItem(text = ''): PlainTextListItem {
  itemCounter += 1;
  return { key: `text-${itemCounter}`, text };
}

type Item = TextListItem | PlainTextListItem;

function asItems(value: unknown, plain: boolean): Item[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item: unknown): Item[] => {
    if (typeof item !== 'object' || item === null) return [];
    const key = 'key' in item && typeof item.key === 'string' ? item.key : '';
    const raw = 'text' in item ? item.text : null;
    if (plain) return key && typeof raw === 'string' ? [{ key, text: raw }] : [];
    const text = zLocalizedText.safeParse(raw);
    return key && text.success ? [{ key, text: text.data }] : [];
  });
}

function itemText(item: Item, language: Language): string {
  return typeof item.text === 'string' ? item.text : localizedTextOf(item.text, language);
}

function withItemText(item: Item, language: Language, text: string): Item {
  return typeof item.text === 'string'
    ? { key: item.key, text }
    : { key: item.key, text: { ...item.text, [language]: text } };
}

export interface TextListFieldProps<TValues extends FieldValues> {
  /** Поле контракта (`theses`): ошибки сервера `поле.N[.язык]` — под строку N. */
  name: FieldPath<TValues>;
  label: string;
  hint?: string;
  placeholder?: string;
  addLabel: string;
  maxItems: number;
  maxLength: number;
  /** Подпись при достигнутом пределе («Больше 8 тезисов добавить нельзя»). */
  limitText: string;
  /** Строки без языка (`PlainTextListItem`) — значения настроек интеграции (`string_list`). */
  plain?: boolean;
  /** Номера строк слева (по умолчанию — да, как у тезисов). */
  numbered?: boolean;
  required?: boolean;
  /** Элемент справа от подписи (точка изменённого поля), если нет переключателя языка. */
  aside?: ReactNode;
}

/**
 * Список коротких текстов (прототип `theses`: «Тезисы курса», до 8): строка — номер, ввод 34 и «×»;
 * «+ Добавить тезис» — новая строка в конце; на пределе — подпись вместо кнопки. Язык ввода
 * (World) — один переключатель на поле.
 */
export function TextListField<TValues extends FieldValues>({
  name,
  label,
  hint,
  placeholder,
  addLabel,
  maxItems,
  maxLength,
  limitText,
  plain = false,
  numbered = true,
  required,
  aside,
}: TextListFieldProps<TValues>) {
  const { t } = useTranslation('form');
  const { languages } = useContentLanguages();
  const multilingual = !plain && languages.length > 1;
  const [active, setActive] = useState<Language>(languages[0] ?? 'ru');
  const language = languages.includes(active) ? active : (languages[0] ?? active);
  const { control, formState } = useFormContext<TValues>();
  const { field, fieldState } = useController({ name, control });
  const items = asItems(field.value, plain);
  const names = useMemo(
    () => [
      name,
      ...items.flatMap((_, index) => [
        `${name}.${index}`,
        ...languages.map((code) => `${name}.${index}.${code}`),
      ]),
    ],
    [name, items, languages],
  );
  useRegisterFields(names);

  const update = (next: Item[]) => field.onChange(next);
  // Новая строка получает фокус: ввод продолжается в ней, а не в прежнем поле.
  const focusKey = useRef<string | null>(null);
  const focusNew = (key: string) => (node: HTMLInputElement | null) => {
    if (node && focusKey.current === key) {
      focusKey.current = null;
      node.focus();
    }
  };

  return (
    <FormField
      label={label}
      required={required}
      error={fieldState.error?.message}
      hint={hint}
      wide
      aside={
        multilingual ? (
          <SegmentedControl
            variant="compact"
            aria-label={t('localized.inputLanguage')}
            value={language}
            onValueChange={(next) => {
              if (isLanguageOf(languages, next)) setActive(next);
            }}
            items={languages.map((code) => ({ value: code, label: t(`localized.short.${code}`) }))}
          />
        ) : (
          aside
        )
      }
    >
      <div className="flex flex-col gap-8">
        {items.map((item, index) => {
          const error = get(formState.errors, `${name}.${index}`);
          const message =
            (typeof error === 'object' && error !== null && 'message' in error
              ? String(error.message ?? '')
              : '') ||
            languages
              .map((code) => nestedErrorMessage(error, code))
              .find((text) => Boolean(text)) ||
            '';
          return (
            <div key={item.key} className="flex flex-col gap-4">
              <div className="flex items-center gap-8">
                {numbered ? (
                  <span
                    aria-hidden="true"
                    className="flex size-26 shrink-0 items-center justify-center rounded-13 bg-surface-sunken text-[12px] font-medium text-wine-bordeaux tabular"
                  >
                    {index + 1}
                  </span>
                ) : null}
                <Input
                  ref={focusNew(item.key)}
                  value={itemText(item, language)}
                  lang={plain ? undefined : language}
                  placeholder={placeholder}
                  maxLength={maxLength}
                  aria-label={t('textList.item', { label, n: index + 1 })}
                  aria-invalid={message ? true : undefined}
                  onChange={(event) =>
                    update(
                      items.map((entry) =>
                        entry.key === item.key
                          ? withItemText(entry, language, event.target.value)
                          : entry,
                      ),
                    )
                  }
                  onBlur={field.onBlur}
                  className="min-w-0 flex-1"
                />
                <button
                  type="button"
                  title={t('textList.remove')}
                  aria-label={t('textList.removeItem', { label, n: index + 1 })}
                  onClick={() => update(items.filter((entry) => entry.key !== item.key))}
                  className="w-16 shrink-0 cursor-pointer border-0 bg-transparent p-0 text-center text-[15px] leading-[18px] text-ink-400 hover:text-status-error"
                >
                  {GLYPH.times}
                </button>
              </div>
              {message ? (
                <span
                  role="alert"
                  className={cn(
                    'text-[11px] leading-[16px] text-status-error',
                    numbered ? 'pl-34' : undefined,
                  )}
                >
                  {message}
                </span>
              ) : null}
            </div>
          );
        })}
        {items.length < maxItems ? (
          <button
            type="button"
            onClick={() => {
              const item = plain
                ? plainTextListItem()
                : textListItem(emptyLocalizedText(languages));
              focusKey.current = item.key;
              update([...items, item]);
            }}
            className="cursor-pointer self-start border-0 bg-transparent p-0 text-[12px] font-medium text-wine-bordeaux hover:underline"
          >
            {addLabel}
          </button>
        ) : (
          <span className="text-[11px] text-ink-500">{limitText}</span>
        )}
      </div>
    </FormField>
  );
}
